import React, { useEffect, useState, useCallback } from 'react';
import FlyerViewer from '../../components/FlyerViewer';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  BookMarked, Library, Star, FolderOpen, FolderPlus, Trash2, Pencil, ExternalLink,
  Printer, Mail, X, Heart, Plus, Loader2, LayoutGrid, Users, Share2, Download, BadgeCheck,
} from 'lucide-react';

const TYPE_LABELS = {
  resource: 'Community Resources',
  legal_form: 'Advance Directives & Legal Forms',
  behavioral_health: 'Behavioral Health Forms',
  social_work_form: 'Social Work Forms',
};
const TYPE_LINK = {
  resource: '/community/resources',
  legal_form: '/advance-directives',
  behavioral_health: '/behavioral-health-directory',
  social_work_form: '/social-work-forms',
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutGrid },
  { key: 'resource', label: 'Community Resources', Icon: Users },
  { key: 'legal_form', label: 'Advance Directives', Icon: BookMarked },
  { key: 'behavioral_health', label: 'Behavioral Health', Icon: BookMarked },
  { key: 'social_work_form', label: 'Social Work Forms', Icon: BookMarked },
  { key: 'favorites', label: 'Favorites', Icon: Star },
  { key: 'collections', label: 'My Collections', Icon: FolderOpen },
];

const printItem = (item) => {
  const w = window.open('', '_blank', 'width=720,height=800');
  if (!w) return;
  w.document.write(`<html><head><title>${item.title}</title>
    <style>body{font-family:Georgia,serif;padding:40px;color:#1f2937;line-height:1.6}
    h1{color:#14532d;font-size:22px;margin-bottom:4px}.cat{color:#b45309;font-size:12px;text-transform:uppercase;font-weight:700}
    .meta{color:#4b5563;font-size:14px;margin:6px 0}.foot{margin-top:30px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:12px;color:#6b7280}</style>
    </head><body>
    <div class="cat">${TYPE_LABELS[item.item_type] || 'Saved Item'}</div>
    <h1>${item.title || ''}</h1>
    ${item.subtitle ? `<div class="meta">${item.subtitle}</div>` : ''}
    ${item.category ? `<div class="meta"><strong>Category:</strong> ${item.category}</div>` : ''}
    ${item.url ? `<div class="meta"><strong>Website:</strong> ${item.url}</div>` : ''}
    <div class="foot">Saved from My INASW Library — INeedASocialWorker.com</div>
    </body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
};

const downloadItem = (item) => {
  const lines = [
    (TYPE_LABELS[item.item_type] || 'Saved Item').toUpperCase(),
    item.title || '', '',
    item.subtitle ? `Details: ${item.subtitle}` : '',
    item.category ? `Category: ${item.category}` : '',
    item.url ? `Website: ${item.url}` : '',
    '', '— Saved from My INASW Library · INeedASocialWorker.com',
  ].filter(Boolean);
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${(item.title || 'inasw-resource').replace(/[^a-z0-9]+/gi, '-').slice(0, 60)}.txt`;
  a.click(); URL.revokeObjectURL(a.href);
};

const shareItem = async (item) => {
  const url = item.url || window.location.origin + '/my-library';
  const data = { title: item.title, text: `${item.title} — via INeedASocialWorker.com`, url };
  try {
    if (navigator.share) { await navigator.share(data); return; }
    await navigator.clipboard.writeText(url); toast.success('Link copied to clipboard.');
  } catch (e) { /* user cancelled */ }
};

const ItemRow = ({ item, onRemove, onFav, onAddToCollection, inCollectionView, onRemoveFromCollection }) => (
  <div className="border rounded-xl p-4 bg-white" data-testid={`library-item-${item.item_id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <span className="text-[11px] font-semibold text-secondary uppercase">{TYPE_LABELS[item.item_type]}</span>
        <h4 className="font-serif text-lg font-bold text-primary truncate">{item.title}</h4>
        {(item.subtitle || item.category) && <p className="text-xs text-gray-500">{[item.subtitle, item.category].filter(Boolean).join(' · ')}</p>}
      </div>
      <div className="flex items-center gap-1 no-print">
        <button onClick={() => onFav(item)} title="Favorite" className={`p-1 ${item.favorite ? 'text-red-500' : 'text-gray-300 hover:text-red-400'}`} data-testid={`fav-${item.item_id}`}>
          <Heart className="w-4 h-4" fill={item.favorite ? 'currentColor' : 'none'} />
        </button>
        {inCollectionView
          ? <button onClick={() => onRemoveFromCollection(item)} title="Remove from collection" className="p-1 text-gray-300 hover:text-red-600" data-testid={`col-remove-${item.item_id}`}><X className="w-4 h-4" /></button>
          : <button onClick={() => onRemove(item)} title="Remove from library" className="p-1 text-gray-300 hover:text-red-600" data-testid={`remove-${item.item_id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-2 no-print">
      {item.url && <a href={item.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</Button></a>}
      <Button size="sm" variant="outline" onClick={() => printItem(item)} data-testid={`print-${item.item_id}`}><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
      <Button size="sm" variant="outline" onClick={() => shareItem(item)} data-testid={`share-${item.item_id}`}><Share2 className="w-3.5 h-3.5 mr-1" /> Share</Button>
      <Button size="sm" variant="outline" onClick={() => downloadItem(item)} data-testid={`download-${item.item_id}`}><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
      {onAddToCollection && <Button size="sm" variant="outline" onClick={() => onAddToCollection(item)} data-testid={`add-to-col-${item.item_id}`}><FolderPlus className="w-3.5 h-3.5 mr-1" /> Add to Collection</Button>}
    </div>
    {item.url && <p className="hidden print:block text-xs text-gray-500 mt-1">{item.url}</p>}
  </div>
);

const MyLibrary = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'dashboard';
  const setTab = (t) => setParams({ tab: t });

  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeCol, setActiveCol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCol, setNewCol] = useState('');

  // add-to-collection dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addItem, setAddItem] = useState(null);
  // email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ client_email: '', client_name: '', message: '' });
  const [sending, setSending] = useState(false);

  const loadDash = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/library/dashboard`); setDash(r.data); setCollections(r.data.collections || []); }
    catch (e) { /* ignore */ }
  }, []);

  const loadItems = useCallback(async (t) => {
    setLoading(true);
    try {
      let url = `${API_URL}/library/items`;
      if (t === 'favorites') url += '?favorite=true';
      else url += `?item_type=${t}`;
      const r = await axios.get(url); setItems(r.data.items || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, []);

  const loadCollections = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/library/collections`); setCollections(r.data.collections || []); }
    catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { document.title = 'My INASW Library | I Need A Social Worker'; }, []);

  useEffect(() => {
    setActiveCol(null);
    if (tab === 'dashboard') { setLoading(true); loadDash().finally(() => setLoading(false)); }
    else if (tab === 'collections') { setLoading(true); loadCollections().finally(() => setLoading(false)); }
    else loadItems(tab);
  }, [tab, loadDash, loadItems, loadCollections]);

  const removeItem = async (item) => {
    if (!window.confirm('Remove this from your library?')) return;
    try { await axios.delete(`${API_URL}/library/items/${item.item_id}`); setItems((p) => p.filter((i) => i.item_id !== item.item_id)); toast.success('Removed.'); }
    catch (e) { toast.error('Could not remove.'); }
  };

  const toggleFav = async (item) => {
    const next = !item.favorite;
    try {
      await axios.put(`${API_URL}/library/items/${item.item_id}`, { favorite: next });
      setItems((p) => tab === 'favorites' && !next ? p.filter((i) => i.item_id !== item.item_id) : p.map((i) => i.item_id === item.item_id ? { ...i, favorite: next } : i));
      if (activeCol) setActiveCol((c) => ({ ...c, items: c.items.map((i) => i.item_id === item.item_id ? { ...i, favorite: next } : i) }));
    } catch (e) { toast.error('Could not update.'); }
  };

  const createCollection = async () => {
    if (!newCol.trim()) return;
    try { await axios.post(`${API_URL}/library/collections`, { name: newCol.trim() }); setNewCol(''); toast.success('Collection created.'); await loadCollections(); }
    catch (e) { toast.error('Could not create.'); }
  };
  const renameCollection = async (c) => {
    const name = window.prompt('Rename collection:', c.name);
    if (!name || !name.trim() || name.trim() === c.name) return;
    try { await axios.put(`${API_URL}/library/collections/${c.collection_id}`, { name: name.trim() }); await loadCollections(); if (activeCol?.collection_id === c.collection_id) openCollection(c.collection_id); toast.success('Renamed.'); }
    catch (e) { toast.error('Could not rename.'); }
  };
  const deleteCollection = async (c) => {
    if (!window.confirm('Delete this collection? (Saved items stay in your library.)')) return;
    try { await axios.delete(`${API_URL}/library/collections/${c.collection_id}`); if (activeCol?.collection_id === c.collection_id) setActiveCol(null); await loadCollections(); toast.success('Deleted.'); }
    catch (e) { toast.error('Could not delete.'); }
  };
  const openCollection = async (id) => {
    try { const r = await axios.get(`${API_URL}/library/collections/${id}`); setActiveCol(r.data.collection); }
    catch (e) { toast.error('Could not open.'); }
  };
  const removeFromCollection = async (item) => {
    try { await axios.post(`${API_URL}/library/collections/${activeCol.collection_id}/remove`, { item_id: item.item_id }); openCollection(activeCol.collection_id); loadCollections(); }
    catch (e) { toast.error('Could not remove.'); }
  };

  const openAdd = async (item) => { setAddItem(item); await loadCollections(); setAddOpen(true); };
  const addToCol = async (c) => {
    try { await axios.post(`${API_URL}/library/collections/${c.collection_id}/add`, { item_id: addItem.item_id }); toast.success(`Added to "${c.name}".`); await loadCollections(); setAddOpen(false); }
    catch (e) { toast.error('Could not add.'); }
  };
  const createColAndAdd = async () => {
    if (!newCol.trim()) return;
    try {
      const r = await axios.post(`${API_URL}/library/collections`, { name: newCol.trim() });
      await axios.post(`${API_URL}/library/collections/${r.data.collection.collection_id}/add`, { item_id: addItem.item_id });
      setNewCol(''); toast.success('Added.'); await loadCollections(); setAddOpen(false);
    } catch (e) { toast.error('Could not create.'); }
  };

  const sendEmail = async () => {
    setSending(true);
    try { const r = await axios.post(`${API_URL}/library/collections/${activeCol.collection_id}/email`, emailForm); toast.success(r.data.message); setEmailOpen(false); setEmailForm({ client_email: '', client_name: '', message: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send.'); } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8 text-white no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><Library className="w-4 h-4" /> Free member hub</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mt-1" data-testid="library-title">My INASW Library</h1>
          <p className="text-white/80 mt-1">Your personalized social work resource center — save forms, assessments, and resources from anywhere on INASW into one place.</p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/10 border border-white/25 text-white text-sm font-medium px-4 py-2" data-testid="library-free-note">
            <BadgeCheck className="w-4 h-4 text-secondary shrink-0" />
            <span>The INASW Library is always free — you&rsquo;re never charged to save or access your materials.</span>
          </div>
        </div>
      </section>

      {/* INASW My Library flyer */}
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <FlyerViewer src="/flyer-my-library.png" alt="My INASW Library Portal" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-5">
        {/* Tabs */}
        <div className="bg-white rounded-2xl border shadow-sm p-2 flex flex-wrap gap-1 no-print" data-testid="library-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 ${tab === t.key ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`library-tab-${t.key}`}>
              <t.Icon className="w-4 h-4" /> {t.label}
              {dash && t.key !== 'dashboard' && t.key !== 'collections' && t.key !== 'favorites' && dash.counts[t.key] > 0 && <span className="ml-0.5 text-xs opacity-70">({dash.counts[t.key]})</span>}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : tab === 'dashboard' ? (
            <DashboardView dash={dash} setTab={setTab} openCollection={(id) => { setTab('collections'); setTimeout(() => openCollection(id), 50); }} />
          ) : tab === 'collections' ? (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 no-print">
                <div className="bg-white rounded-2xl border p-5">
                  <div className="flex gap-2 mb-4">
                    <Input placeholder="New collection (e.g. Hospice)" value={newCol} onChange={(e) => setNewCol(e.target.value)} data-testid="new-collection-input" />
                    <Button className="bg-primary text-white" onClick={createCollection} data-testid="create-collection-btn"><FolderPlus className="w-4 h-4" /></Button>
                  </div>
                  {collections.length === 0 ? <p className="text-sm text-gray-500 text-center py-6">No collections yet. Mix items from every database into one folder, e.g. "Hospice Admission Packet".</p> : (
                    <ul className="space-y-2">
                      {collections.map((c) => (
                        <li key={c.collection_id}>
                          <div className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer ${activeCol?.collection_id === c.collection_id ? 'border-primary bg-primary/5' : 'hover:border-gray-300'}`} onClick={() => openCollection(c.collection_id)} data-testid={`collection-${c.collection_id}`}>
                            <span className="text-gray-800 text-sm">{c.name} <span className="text-xs text-gray-400">({c.item_count})</span></span>
                            <span className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); renameCollection(c); }} className="text-gray-400 hover:text-primary p-1"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={(e) => { e.stopPropagation(); deleteCollection(c); }} className="text-gray-400 hover:text-red-600 p-1" data-testid={`delete-collection-${c.collection_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="lg:col-span-2">
                {!activeCol ? (
                  <div className="bg-white rounded-2xl border p-10 text-center text-gray-500 no-print">Select a collection to view, print, or email its items.</div>
                ) : (
                  <div className="bg-white rounded-2xl border p-6" id="printable-collection">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                      <h2 className="font-serif text-2xl font-bold text-primary">{activeCol.name}</h2>
                      <div className="flex gap-2 no-print">
                        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-collection-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                        <Button size="sm" className="bg-secondary text-white" onClick={() => setEmailOpen(true)} data-testid="email-collection-btn"><Mail className="w-4 h-4 mr-1" /> Email</Button>
                      </div>
                    </div>
                    {(activeCol.items || []).length === 0 ? <p className="text-gray-500 text-sm text-center py-8">This collection is empty. Add saved items using "Add to Collection".</p> : (
                      <div className="space-y-3">
                        {activeCol.items.map((i) => <ItemRow key={i.item_id} item={i} onFav={toggleFav} inCollectionView onRemoveFromCollection={removeFromCollection} />)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // type tabs + favorites
            items.length === 0 ? (
              <div className="bg-white rounded-xl border p-10 text-center">
                <BookMarked className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Nothing saved here yet.</p>
                {TYPE_LINK[tab] && <Link to={TYPE_LINK[tab]} className="text-secondary underline text-sm mt-1 inline-block">Browse the directory →</Link>}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((i) => <ItemRow key={i.item_id} item={i} onRemove={removeItem} onFav={toggleFav} onAddToCollection={openAdd} />)}
              </div>
            )
          )}
        </div>
      </div>

      {/* Add to collection dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add to a Collection</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {collections.length === 0 && <p className="text-sm text-gray-400">No collections yet — create one below.</p>}
            {collections.map((c) => (
              <button key={c.collection_id} onClick={() => addToCol(c)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg border hover:border-primary text-left" data-testid={`add-option-${c.collection_id}`}>
                <span className="text-gray-800 text-sm">{c.name} <span className="text-xs text-gray-400">({c.item_count})</span></span>
                <Plus className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Input placeholder="New collection name" value={newCol} onChange={(e) => setNewCol(e.target.value)} data-testid="add-new-collection-name" />
            <Button className="bg-primary text-white" onClick={createColAndAdd} disabled={!newCol.trim()}><FolderPlus className="w-4 h-4 mr-1" /> Create &amp; Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Email this Collection</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500 mb-1 block">Recipient email *</Label><Input type="email" value={emailForm.client_email} onChange={(e) => setEmailForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="email-recipient" placeholder="client@example.com" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Recipient name</Label><Input value={emailForm.client_name} onChange={(e) => setEmailForm((f) => ({ ...f, client_name: e.target.value }))} /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Personal message (optional)</Label><Textarea rows={3} value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={sendEmail} disabled={sending} data-testid="send-collection-email"><Mail className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DashboardView = ({ dash, setTab, openCollection }) => {
  if (!dash) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(TYPE_LABELS).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className="bg-white rounded-2xl border p-4 text-left hover:border-secondary transition-colors" data-testid={`dash-count-${k}`}>
            <p className="text-3xl font-bold text-primary">{dash.counts[k] || 0}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-serif text-lg font-bold text-primary mb-3">Recently saved</h3>
          {(dash.recent || []).length === 0 ? <p className="text-sm text-gray-500">Nothing saved yet. Save items from any directory to build your library.</p> : (
            <ul className="space-y-2">
              {dash.recent.map((i) => (
                <li key={i.item_id} className="flex items-center justify-between border-b last:border-0 pb-2">
                  <div className="min-w-0"><p className="text-sm font-medium text-gray-800 truncate">{i.title}</p><p className="text-[11px] text-secondary uppercase">{TYPE_LABELS[i.item_type]}</p></div>
                  {i.url && <a href={i.url} target="_blank" rel="noreferrer" className="text-primary"><ExternalLink className="w-4 h-4" /></a>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg font-bold text-primary">My Collections</h3>
            <button onClick={() => setTab('collections')} className="text-xs text-primary hover:underline">Manage →</button>
          </div>
          {(dash.collections || []).length === 0 ? <p className="text-sm text-gray-500">Create collections like "Hospice Admission Packet" that mix resources, legal forms, and assessments.</p> : (
            <div className="flex flex-wrap gap-2">
              {dash.collections.map((c) => (
                <button key={c.collection_id} onClick={() => openCollection(c.collection_id)} className="px-3 py-1.5 rounded-full bg-primary/5 border border-primary/20 text-sm text-primary hover:bg-primary/10" data-testid={`dash-collection-${c.collection_id}`}>
                  {c.name} <span className="text-xs text-gray-400">({c.item_count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyLibrary;
